/**
 * Data transformation utilities for recurring transactions
 *
 * Transforms data from the useRecurringTransactions hook output format
 * to the simplified format expected by the RecurringPage components.
 */

import type { RecurringTransaction } from '@/hooks/useRecurringTransactions';
import { fromDateString, toDateString } from '@/utils/dateUtils';

export interface TransformedRecurringTransaction {
  id: string;
  description: string;
  amount: number;
  type: "income" | "expense";
  category: string;
  pattern: {
    type: "daily" | "weekly" | "monthly" | "yearly";
    interval: number;
  };
  next_date: string;
  start_date: string;
  max_occurrences: number | null;
  current_occurrences: number;
  is_active: boolean;
  remaining_occurrences: number | null;
}

/**
 * Safely parse a date value, returning null if invalid
 */
function safeParseDate(dateValue: Date | string | null | undefined): Date | null {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (typeof dateValue === 'string') {
      // Use fromDateString for safe parsing
      const parsed = fromDateString(dateValue);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Calculate if a recurring transaction is active with proper error handling
 */
function calculateActiveStatus(transaction: RecurringTransaction): boolean {
  try {
    // A transaction is active if it's marked as recurring and:
    // 1. It has no end date, OR
    // 2. The end date is in the future, OR
    // 3. It hasn't reached its maximum count yet

    if (!transaction.is_recurring) return false;

    const now = new Date();

    // Check end date with validation using safeParseDate
    if (transaction.recurrence_end_date) {
      const endDate = safeParseDate(transaction.recurrence_end_date);
      if (!endDate) {
        // If we can't parse the end date, assume it's active if it's recurring
        return true;
      }
      if (endDate < now) return false;
    }

    // Check occurrence count
    if (transaction.recurrence_count && transaction.children_count) {
      if (transaction.children_count >= transaction.recurrence_count) return false;
    }

    return true;
  } catch (error) {
    console.error('Error calculating active status for transaction:', transaction.id, error);
    // Default to false if we can't determine status
    return false;
  }
}

/**
 * Calculate remaining occurrences for a recurring transaction
 */
function calculateRemainingOccurrences(transaction: RecurringTransaction): number | null {
  // If no max occurrences set, return null (unlimited)
  if (!transaction.recurrence_count) return null;

  // Calculate remaining based on current count
  const current = transaction.children_count || 0;
  const remaining = transaction.recurrence_count - current;

  return Math.max(0, remaining);
}

/**
 * Transform a single recurring transaction from hook format to component format with error handling
 */
export function transformRecurringTransaction(transaction: RecurringTransaction): TransformedRecurringTransaction {
  try {
  // Format dates to YYYY-MM-DD strings using safeParseDate and toDateString utilities
  const formatDate = (date: Date | string | null | undefined): string => {
    const parsed = safeParseDate(date);
    return parsed ? toDateString(parsed) : '';
  };

  // Get category name from the transaction or use default
  const categoryName = transaction.category || 'Sem Categoria';

  return {
    id: transaction.id,
    description: transaction.description || 'Sem descrição',
    amount: transaction.amount || 0,
    type: transaction.type === 'income' ? 'income' : 'expense',
    category: categoryName,
    pattern: {
      type: transaction.recurrence_pattern as "daily" | "weekly" | "monthly" | "yearly",
      interval: transaction.recurrence_interval || 1,
    },
    next_date: formatDate(transaction.next_occurrence),
    start_date: formatDate(transaction.date),
    max_occurrences: transaction.recurrence_count || null,
    current_occurrences: transaction.children_count || 0,
    is_active: calculateActiveStatus(transaction),
    remaining_occurrences: calculateRemainingOccurrences(transaction),
  };
  } catch (error) {
    console.error('Error transforming recurring transaction:', transaction.id, error);
    // Return a safe default object
    return {
      id: transaction.id || 'unknown',
      description: transaction.description || 'Erro ao carregar transação',
      amount: 0,
      type: 'expense',
      category: 'Sem Categoria',
      pattern: {
        type: 'monthly',
        interval: 1,
      },
      next_date: '',
      start_date: '',
      max_occurrences: null,
      current_occurrences: 0,
      is_active: false,
      remaining_occurrences: null,
    };
  }
}

/**
 * Transform an array of recurring transactions with error handling
 */
export function transformRecurringTransactions(transactions: RecurringTransaction[]): TransformedRecurringTransaction[] {
  if (!Array.isArray(transactions)) {
    console.warn('Invalid transactions array:', transactions);
    return [];
  }

  return transactions.map((transaction, index) => {
    try {
      return transformRecurringTransaction(transaction);
    } catch (error) {
      console.error(`Error transforming transaction at index ${index}:`, transaction, error);
      // Return a safe fallback for this specific transaction
      return {
        id: transaction?.id || `error-${index}`,
        description: 'Erro ao carregar transação',
        amount: 0,
        type: 'expense' as const,
        category: 'Sem Categoria',
        pattern: {
          type: 'monthly' as const,
          interval: 1,
        },
        next_date: '',
        start_date: '',
        max_occurrences: null,
        current_occurrences: 0,
        is_active: false,
        remaining_occurrences: null,
      };
    }
  });
}

/**
 * Calculate statistics from transformed recurring transactions
 */
export function calculateRecurringStatistics(transactions: TransformedRecurringTransaction[]) {
  const activeTransactions = transactions.filter(tx => tx.is_active);
  const monthlyTotal = transactions
    .filter(tx => tx.pattern.type === 'monthly')
    .reduce((sum, tx) => sum + tx.amount, 0);

  const nextTransaction = activeTransactions
    .reduce((earliest, tx) => {
      if (!tx.next_date) return earliest;
      if (!earliest || new Date(tx.next_date) < new Date(earliest.next_date)) {
        return tx;
      }
      return earliest;
    }, null as TransformedRecurringTransaction | null);

  const recentTransactions = activeTransactions
    .filter(tx => tx.next_date)
    .sort((a, b) => new Date(a.next_date!).getTime() - new Date(b.next_date!).getTime())
    .slice(0, 5);

  return {
    activeRecurringCount: activeTransactions.length,
    monthlyTotal,
    nextTransaction,
    recentTransactions,
  };
}